import React, { useState, useEffect } from 'react';
import { Terminal, Shield, Brain, Cpu, Activity, Zap } from 'lucide-react';
import Spline from '@splinetool/react-spline';
import { personalInfo } from '../mock';

const Hero = () => {
  const [text, setText] = useState('');
  const [showContent, setShowContent] = useState(false);
  const initSequence = ['> INIT GOLDHAT.PROTOCOL', '> LOADING IDENTITY…', '> ONLINE.'];
  const [sequenceIndex, setSequenceIndex] = useState(0);
  const [glitchActive, setGlitchActive] = useState(false);
  const [systemStats, setSystemStats] = useState({
    cpu: 87,
    memory: 92,
    network: 99
  });

  useEffect(() => {
    let timeout;
    if (sequenceIndex < initSequence.length) {
      timeout = setTimeout(() => {
        setText(initSequence[sequenceIndex]);
        setSequenceIndex(prev => prev + 1);
      }, 800);
    } else {
      timeout = setTimeout(() => {
        setShowContent(true);
      }, 500);
    }
    return () => clearTimeout(timeout);
  }, [sequenceIndex]);

  // Glitch effect on name
  useEffect(() => {
    if (showContent) {
      const glitchInterval = setInterval(() => {
        setGlitchActive(true);
        setTimeout(() => setGlitchActive(false), 150);
      }, 4000);
      return () => clearInterval(glitchInterval);
    }
  }, [showContent]);

  // Simulate system stats
  useEffect(() => {
    if (showContent) {
      const statsInterval = setInterval(() => {
        setSystemStats({
          cpu: 85 + Math.floor(Math.random() * 10),
          memory: 90 + Math.floor(Math.random() * 8),
          network: 97 + Math.floor(Math.random() * 3)
        });
      }, 2000);
      return () => clearInterval(statsInterval);
    }
  }, [showContent]);

  return (
    <div className="hero-container" style={{ minHeight: '100vh', position: 'relative' }}>
      {/* Cyberpunk grid overlay */}
      <div style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        backgroundImage: `
          repeating-linear-gradient(0deg, transparent, transparent 49px, rgba(0, 255, 209, 0.03) 49px, rgba(0, 255, 209, 0.03) 50px),
          repeating-linear-gradient(90deg, transparent, transparent 49px, rgba(0, 255, 209, 0.03) 49px, rgba(0, 255, 209, 0.03) 50px)
        `,
        pointerEvents: 'none',
        zIndex: 0
      }} />

      {/* Scanning line effect */}
      <div style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '3px',
        background: 'linear-gradient(90deg, transparent, #00FFD1, transparent)',
        boxShadow: '0 0 20px #00FFD1',
        animation: 'scanline 4s linear infinite',
        zIndex: 2,
        pointerEvents: 'none'
      }} />

      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        minHeight: '100vh',
        padding: '0 7.6923%',
        gap: '60px',
        position: 'relative',
        zIndex: 1
      }}>
        {/* Left content */}
        <div style={{ flex: 1, maxWidth: '600px', position: 'relative' }}>
          {/* Corner brackets - cyberpunk UI */}
          <div style={{
            position: 'absolute',
            top: '-20px',
            left: '-20px',
            width: '60px',
            height: '60px',
            borderTop: '2px solid #00FFD1',
            borderLeft: '2px solid #00FFD1',
            opacity: showContent ? 1 : 0,
            transition: 'opacity 1s ease'
          }} />
          <div style={{
            position: 'absolute',
            bottom: '-20px',
            right: '-20px',
            width: '60px',
            height: '60px',
            borderBottom: '2px solid #00FFD1',
            borderRight: '2px solid #00FFD1',
            opacity: showContent ? 1 : 0,
            transition: 'opacity 1s ease'
          }} />

          {/* Terminal init sequence */}
          <div style={{
            fontFamily: 'monospace',
            color: '#00FFD1',
            fontSize: '14px',
            marginBottom: '40px',
            minHeight: '60px',
            opacity: sequenceIndex < initSequence.length ? 1 : 0,
            transition: 'opacity 0.5s ease',
            textShadow: '0 0 10px rgba(0, 255, 209, 0.8)'
          }}>
            {text && <div style={{ marginBottom: '8px' }}>{text}</div>}
          </div>

          {/* Main content */}
          <div style={{
            opacity: showContent ? 1 : 0,
            transform: showContent ? 'translateY(0)' : 'translateY(20px)',
            transition: 'all 0.8s ease-out'
          }}>
            {/* System stats overlay */}
            <div style={{
              display: 'flex',
              gap: '16px',
              marginBottom: '32px',
              fontSize: '11px',
              fontFamily: 'monospace',
              color: 'rgba(0, 255, 209, 0.7)'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <Cpu size={12} />
                <span>CPU: {systemStats.cpu}%</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <Activity size={12} />
                <span>MEM: {systemStats.memory}%</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <Zap size={12} />
                <span>NET: {systemStats.network}%</span>
              </div>
            </div>

            <h1 style={{
              fontSize: '66px',
              fontWeight: 600,
              lineHeight: 1.1,
              letterSpacing: '-0.62px',
              color: '#FFFFFF',
              marginBottom: '24px',
              textShadow: glitchActive 
                ? '3px 3px 0 #FF0055, -3px -3px 0 #00FFD1' 
                : '0 0 20px rgba(0, 255, 209, 0.3)',
              animation: glitchActive ? 'glitch 0.3s ease' : 'none',
              position: 'relative'
            }}>
              {personalInfo.name}
              {/* Glitch overlay */}
              {glitchActive && (
                <div style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  background: 'rgba(0, 255, 209, 0.1)',
                  mixBlendMode: 'screen',
                  pointerEvents: 'none'
                }} />
              )}
            </h1>
            
            <div style={{
              fontSize: '32px',
              fontWeight: 600,
              lineHeight: 1.25,
              color: '#00FFD1',
              marginBottom: '32px',
              textShadow: '0 0 20px rgba(0, 255, 209, 0.3)'
            }}>
              The {personalInfo.alias} Engineer
            </div>

            <p style={{
              fontSize: '18px',
              fontWeight: 400,
              lineHeight: 1.5,
              color: 'rgba(255, 255, 255, 0.85)',
              marginBottom: '48px',
              maxWidth: '500px'
            }}>
              {personalInfo.role}
            </p>

            {/* CTA Buttons */}
            <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
              <button
                onClick={() => document.getElementById('projects')?.scrollIntoView({ behavior: 'smooth' })}
                style={{
                  background: '#00FFD1',
                  color: '#000000',
                  border: 'none',
                  borderRadius: '0px',
                  padding: '14px 24px',
                  fontSize: '18px',
                  fontWeight: 500,
                  cursor: 'pointer',
                  transition: 'all 0.4s ease-in-out',
                  minHeight: '56px',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '12px'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'rgba(0, 255, 209, 0.1)';
                  e.currentTarget.style.color = '#00FFD1';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = '#00FFD1';
                  e.currentTarget.style.color = '#000000';
                }}
              >
                <Terminal size={20} />
                ./projects
              </button>

              <button
                onClick={() => document.getElementById('skills')?.scrollIntoView({ behavior: 'smooth' })}
                style={{
                  background: 'rgba(255, 255, 255, 0.1)',
                  color: '#FFFFFF',
                  border: 'none',
                  borderRadius: '0px',
                  padding: '14px 24px',
                  fontSize: '18px',
                  fontWeight: 500,
                  cursor: 'pointer',
                  transition: 'all 0.4s ease-in-out',
                  minHeight: '56px',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '12px'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = '#FFFFFF';
                  e.currentTarget.style.color = '#000000';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
                  e.currentTarget.style.color = '#FFFFFF';
                }}
              >
                <Shield size={20} />
                ./skills
              </button>

              <button
                onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })}
                style={{
                  background: 'rgba(255, 255, 255, 0.1)',
                  color: '#FFFFFF',
                  border: 'none',
                  borderRadius: '0px',
                  padding: '14px 24px',
                  fontSize: '18px',
                  fontWeight: 500,
                  cursor: 'pointer',
                  transition: 'all 0.4s ease-in-out',
                  minHeight: '56px',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '12px'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = '#FFFFFF';
                  e.currentTarget.style.color = '#000000';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
                  e.currentTarget.style.color = '#FFFFFF';
                }}
              >
                <Brain size={20} />
                ./contact
              </button>
            </div>

            {/* Status indicators - Enhanced hacker style */}
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '12px',
              marginTop: '48px',
              fontSize: '13px',
              fontFamily: 'monospace',
              padding: '24px',
              background: 'rgba(0, 255, 209, 0.03)',
              border: '1px solid rgba(0, 255, 102, 0.49)',
              position: 'relative'
            }}>
              {/* Corner accents */}
              <div style={{
                position: 'absolute',
                top: '-1px',
                left: '-1px',
                width: '20px',
                height: '20px',
                borderTop: '2px solid #00FFD1',
                borderLeft: '2px solid #00FFD1'
              }} />
              <div style={{
                position: 'absolute',
                bottom: '-1px',
                right: '-1px',
                width: '20px',
                height: '20px',
                borderBottom: '2px solid #00FFD1',
                borderRight: '2px solid #00FFD1'
              }} />

              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <div style={{
                  width: '8px',
                  height: '8px',
                  background: '#00FFD1',
                  boxShadow: '0 0 10px rgba(0, 255, 209, 0.8)',
                  animation: 'pulse 2s infinite'
                }} />
                <span style={{ color: '#00FFD1' }}>{'>'}</span>
                <span style={{ color: 'rgba(255, 255, 255, 0.9)' }}>SYSTEM_STATUS: ONLINE</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <div style={{
                  width: '8px',
                  height: '8px',
                  background: '#00FFD1',
                  boxShadow: '0 0 10px rgba(0, 255, 209, 0.8)'
                }} />
                <span style={{ color: '#00FFD1' }}>{'>'}</span>
                <span style={{ color: 'rgba(255, 255, 255, 0.9)' }}>CLEARANCE_LEVEL: {personalInfo.clearance}</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <div style={{
                  width: '8px',
                  height: '8px',
                  background: '#00FFD1',
                  boxShadow: '0 0 10px rgba(0, 255, 209, 0.8)'
                }} />
                <span style={{ color: '#00FFD1' }}>{'>'}</span>
                <span style={{ color: 'rgba(255, 255, 255, 0.9)' }}>LOCATION: {personalInfo.location}</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <div style={{
                  width: '8px',
                  height: '8px',
                  background: '#00FFD1',
                  boxShadow: '0 0 10px rgba(0, 255, 209, 0.8)'
                }} />
                <span style={{ color: '#00FFD1' }}>{'>'}</span>
                <span style={{ color: 'rgba(255, 255, 255, 0.9)' }}>ENCRYPTION: AES-256 | RSA-4096</span>
              </div>
            </div>

            {/* Code snippet decoration */}
            <div style={{
              marginTop: '32px',
              padding: '16px',
              background: 'rgba(0, 0, 0, 0.6)',
              border: '1px solid rgba(0, 255, 102, 0.49)',
              fontSize: '11px',
              fontFamily: 'monospace',
              color: 'rgba(0, 255, 209, 0.6)',
              lineHeight: 1.6,
              overflow: 'hidden',
              maxHeight: '100px'
            }}>
              <div>{'// Initializing neural network...'}</div>
              <div>{'const agent = new AutonomousSystem({'}</div>
              <div>{'  mode: "OFFENSIVE_RESEARCH",'}</div>
              <div>{'  target: "INFRASTRUCTURE",'}</div>
              <div>{'  ai_models: ["GPT-5", "CLAUDE-4"]'}</div>
              <div>{'});'}</div>
            </div>
          </div>
        </div>

        {/* Right side - Spline 3D */}
        <div style={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '700px',
          opacity: showContent ? 1 : 0,
          transition: 'opacity 1s ease-out 0.5s'
        }}>
          <div style={{
            width: '700px',
            height: '700px',
            position: 'relative',
            overflow: 'visible'
          }}>
            <Spline scene="https://prod.spline.design/NbVmy6DPLhY-5Lvg/scene.splinecode" />
          </div>
        </div>
      </div>

      <style>{`
        @keyframes pulse {
          0%, 100% {
            opacity: 1;
            transform: scale(1);
          }
          50% {
            opacity: 0.6;
            transform: scale(1.2);
          }
        }

        @keyframes scanline {
          0% {
            top: 0;
          }
          100% {
            top: 100%;
          }
        }

        @keyframes glitch {
          0% {
            transform: translate(0);
          }
          20% {
            transform: translate(-2px, 2px);
          }
          40% {
            transform: translate(-2px, -2px);
          }
          60% {
            transform: translate(2px, 2px);
          }
          80% {
            transform: translate(2px, -2px);
          }
          100% {
            transform: translate(0);
          }
        }

        @media (max-width: 1024px) {
          .hero-container > div {
            flex-direction: column !important;
            text-align: center;
            padding: 100px 20px !important;
          }
          
          .hero-container h1 {
            font-size: 48px !important;
          }
          
          .hero-container > div > div:first-child {
            max-width: 100% !important;
            align-items: center;
          }
          
          .hero-container button {
            justify-content: center;
          }
        }
      `}</style>
    </div>
  );
};

export default Hero;